import React, { ReactNode, Component } from 'react';
import './DocumentsTable.scss';
import { Document, EDocumentType } from '../../models/Document';
import { ESortDirection, ETableColumnAlign, ResponsiveTable, ResponsiveTableColumn } from '../responsive-table/ResponsiveTable';
import Button from 'react-bootstrap/Button';
import { Search } from '../search/Search';
import { DocumentHelper } from '../../helpers/DocumentHelper';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import save from 'save-file';
import { Icon } from '../icon/Icon';
import { RoundButton } from '../round-button/RoundButton';
import { ConfirmModal } from '../confirm-modal/ConfirmModal';

interface DocumentsTableProps {
  documents: Document[];
  documentsType?: EDocumentType;
  memberId?: number;
  allowDelete?: boolean;
  onDeleteDocument?: (document: Document) => Promise<void>;
  onDownloadDocument: (document: Document) => Promise<Blob>;
}

interface DocumentsTableState {
  searchKeys?: string;
  deleteDocument?: Document;
  deleting?: boolean;
  downloadingDocument?: Document;
}

interface IDocumentTableData {
  lastModified: string;
  fileName: JSX.Element;
  size: JSX.Element;
  buttons: JSX.Element;
}

export class DocumentsTable extends Component<DocumentsTableProps, DocumentsTableState> {
  private columns: ResponsiveTableColumn<IDocumentTableData>[] = [];

  constructor(props: DocumentsTableProps) {
    super(props);
    this.state = {};

    this.columns = [
      { title: 'Datum', objectKey: 'lastModified', minWidth: '7rem', customSortFunction: this.sort },
      { title: 'Bestand', objectKey: 'fileName', customSortFunction: this.sort },
      { title: 'Grootte', objectKey: 'size', customSortFunction: this.sort },
      { title: '', objectKey: 'buttons', minWidth: '7.7rem', disableSort: true, hideBorder: true, horizontalAlign: ETableColumnAlign.END },
    ];
  }

  filter = (document: Document): boolean => {
    const sanitizedKeys = this.state.searchKeys?.trim().toLowerCase();
    if (!sanitizedKeys || sanitizedKeys.length === 0) {
      return true;
    } else {
      return document.name.toLowerCase().includes(sanitizedKeys);
    }
  };

  sort = (indexA: number, indexB: number, direction: ESortDirection, objectKey: keyof IDocumentTableData): number => {
    const a = this.props.documents[indexA];
    const b = this.props.documents[indexB];
    const sortDesc = direction === ESortDirection.DESCENDING;
    switch (objectKey) {
      case 'lastModified':
        return sortDesc ? b.lastModified.getTime() - a.lastModified.getTime() : a.lastModified.getTime() - b.lastModified.getTime();
      case 'fileName':
        return sortDesc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      case 'size':
        return sortDesc ? b.bytes - a.bytes : a.bytes - b.bytes;
      default:
        return 0;
    }
  };

  download = (document: Document): void => {
    const { onDownloadDocument } = this.props;
    this.setState({ downloadingDocument: document });
    NotificationHelper.toastSuccess('Download wordt voorbereid...', { silent: true });
    onDownloadDocument(document)
      .then(blob => save(blob, document.name))
      .catch(error => {
        console.error('Failed to provide download', error);
        NotificationHelper.toastError('Downloaden mislukt, probeer het later opnieuw.');
      })
      .finally(() => this.setState({ downloadingDocument: undefined }));
  };

  // eslint-disable-next-line @typescript-eslint/unbound-method
  openInWindow = DocumentHelper.openDocumentInWindow;

  delete = (): void => {
    const { deleteDocument } = this.state;
    if (!this.props.onDeleteDocument) {
      return;
    }
    if (deleteDocument) {
      this.setState({ deleting: true });
      this.props.onDeleteDocument(deleteDocument)
        .then(() => NotificationHelper.toastSuccess('Bestand verwijderd', { silent: true }))
        .catch(error => {
          console.error('Failed to delete document', error);
          NotificationHelper.toastError('Verwijderen mislukt, probeer het later opnieuw.');
        })
        .finally(() => {
          this.setState({ deleting: false });
          this.closeDeleteModal();
        });
    } else {
      this.closeDeleteModal();
      throw new Error('Delete document called while no deleteDocument was set.');
    }
  };

  showDeleteModal = (deleteDocument: Document): void => {
    this.setState({ deleteDocument });
  };

  closeDeleteModal = (): void => {
    this.setState({ deleteDocument: undefined });
  };

  handleSearch = (value: string): void => {
    this.setState({ searchKeys: value });
  };

  handleFileLinkCopy = (document: Document): void => {
    const { documentsType, memberId } = this.props;
    if (!documentsType) {
      throw new Error('Tried creating a filelink without a known documents type');
    }
    navigator.clipboard.writeText(`topp-file//${document.getNameBase64(documentsType, memberId)}`)
      .then(() => {
        NotificationHelper.toastSuccess('Bestandslink succesvol gekopiëerd');
      })
      .catch(() => {
        NotificationHelper.toastError('Kopiëren bestandslink mislukt');
      });
  };

  render(): ReactNode {
    const { documents } = this.props;
    const { deleteDocument, deleting } = this.state;
    const { columns } = this;

    const data: IDocumentTableData[] = documents.filter(this.filter).map(document => {
      const { onDownloadDocument, allowDelete } = this.props;
      const { downloadingDocument } = this.state;
      const { lastModified, name, bytes } = document;
      const localDate = lastModified.toLocaleDateString('nl-NL', {
        year: 'numeric', month: 'long', day: 'numeric'
      });

      return ({
        lastModified: localDate,
        fileName: (
          <>
            <div className="documents-table__file-name">
              <Button variant="link" className="documents-table__open-button" onClick={() => this.openInWindow(document, EDocumentType.SHARED, onDownloadDocument)}>{name}</Button>
              {
                this.props.documentsType &&
                <Button className="documents-table__copy-button" variant="link" title="Kopiëer link" onClick={() => this.handleFileLinkCopy(document)}>{<Icon icon="copy"/>}</Button>
              }
            </div>
          </>),
        size: <>{DocumentHelper.getHumanSize(bytes)}</>,
        buttons: <>
          <RoundButton label="Download" type="download" loading={downloadingDocument?.match(document)} onClick={() => this.download(document)} />
          { allowDelete && <RoundButton label="Verwijder" type="delete" onClick={() => this.showDeleteModal(document)} /> }
        </>,
      });
    });

    return (
      <>
        <div className="sort-search-container">
          <Search onSearch={this.handleSearch} placeholder="Zoek in bestanden" />
        </div>
        <ResponsiveTable<IDocumentTableData> columns={columns} data={data} sortByDefault={columns[0]}/>
        {
          deleteDocument &&
            <ConfirmModal
              title="Bestand verwijderen"
              body={<>Weet je zeker dat je <strong>{deleteDocument?.name}</strong> wilt verwijderen?</>}
              confirmLabel="Verwijderen"
              confirmButtonStyle="danger"
              onCancel={this.closeDeleteModal}
              onConfirm={this.delete}
              confirming={deleting}
            />
        }
      </>
    );
  }
}
